import { api } from '@/lib/api';
import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { mutationKeys } from '../mutationKeys';
import { queryKeys } from '../queryKeys';

export interface CreatePatientCartRequest {
  user_id: number;
  name: string;
  // Optional on the API; the walk-in order flow attaches the ordering doctor
  // and clinic at cart creation.
  doctor_id?: number;
  clinic_id?: number;
}

export interface CreatePatientCartResponse {
  id: number;
  name: string;
  user_id: number;
  period: number;
  state: string | null;
  medications: string | null;
  started: string | null;
  promo: number;
  created_by_doctor: number | null;
}

export interface CreateProtocolOrderCartRequest {
  user_id: number;
  name: string;
}

export interface ProtocolOrderCartItem {
  priceId?: number;
}

export const createPatientCartMutation = () => {
  const queryClient = useQueryClient();

  return mutationOptions({
    mutationKey: mutationKeys.adminPatientCarts.create(),
    mutationFn: async (values: CreatePatientCartRequest) => {
      const response = await api
        .post('admin/patient-carts', {
          json: values,
        })
        .json<{ data: CreatePatientCartResponse }>();
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.orders.index] });
      toast.success('Patient cart created successfully');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to create patient cart');
    },
  });
};

export const createProtocolOrderCartMutation = (
  selectedItems: Array<ProtocolOrderCartItem>,
) => {
  const itemsWithPrice = selectedItems.filter(
    (s): s is ProtocolOrderCartItem & { priceId: number } =>
      s.priceId != null && s.priceId > 0,
  );

  return mutationOptions({
    mutationKey: [...mutationKeys.adminPatientCarts.create(), 'protocol-order'],
    mutationFn: async (values: CreateProtocolOrderCartRequest) => {
      const res = await api
        .post('admin/patient-carts', {
          json: values,
        })
        .json<{ data: CreatePatientCartResponse }>();
      const cartId = res.data.id;
      for (const item of itemsWithPrice) {
        await api.post(`carts/${cartId}/items`, {
          json: { price_id: item.priceId, quantity: 1 },
        });
      }
      return res.data;
    },
  });
};
